import { getBackendUrl } from '../../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import ToolsFilter from '../../components/admin/candidates/ToolsFilter';
import CVReanalyzeButton from '../../components/admin/candidates/CVReanalyzeButton';
import KanbanView from '../../components/admin/candidates/KanbanView';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Loader2, ChevronDown, ChevronUp, LayoutGrid, List, Columns, CheckCircle2, Send, CheckSquare, Square, Ban, History, Eye, EyeOff } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import DecisionCard from '../../components/admin/DecisionCard';
import FilterChips from '../../components/admin/FilterChips';

const CandidatesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // === STATE ===
  const [candidates, setCandidates] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [benchmarks, setBenchmarks] = useState({});
  
  // Animation states
  const [animatingId, setAnimatingId] = useState(null);
  const [animationType, setAnimationType] = useState(null);
  
  // Filters — initial value picked up from URL params (?q=...&scope=skills)
  // Fallback : sessionStorage so search persists when user clicks into a candidate then comes back.
  const SEARCH_STORAGE_KEY = 'tp_candidates_search';
  const initialFromStorage = (() => {
    try {
      const raw = sessionStorage.getItem(SEARCH_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get('q') || initialFromStorage?.q || ''
  );
  const [searchScope, setSearchScope] = useState(
    () => searchParams.get('scope') || initialFromStorage?.scope || 'all'
  );
  const [toolsFilter, setToolsFilter] = useState(
    () => {
      const fromUrl = searchParams.get('tools');
      if (fromUrl) return fromUrl.split(',').filter(Boolean);
      const fromStore = initialFromStorage?.tools;
      return Array.isArray(fromStore) ? fromStore : [];
    }
  );
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    category: '',
    country: '',
    experience_level: '',
    evaluation_status: ''
  });
  
  // View mode (cards vs table - pour desktop)
  const [viewMode, setViewMode] = useState(() => {
    // Persist last-used view mode across sessions: 'cards' | 'table' | 'kanban'
    return sessionStorage.getItem('candidates_view_mode') || 'cards';
  });

  // Show all statuses (override the default exclude_status filter)
  const [showAllStatuses, setShowAllStatuses] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendingBulk, setSendingBulk] = useState(false);
  
  // Counts pour les filtres
  const [filterCounts, setFilterCounts] = useState({
    priority: 0,
    recommended: 0,
    new: 0,
    budgetOk: 0,
    all: 0
  });

  // === FETCH DATA ===
  useEffect(() => {
    fetchCategories();
    fetchCountries();
    fetchBenchmarks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCandidates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, advancedFilters, searchQuery, searchScope, toolsFilter, showAllStatuses]);

  // Sync URL params with search state (so F5 / share-link / browser-back keep query)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (searchQuery) {
      next.set('q', searchQuery);
      next.set('scope', searchScope);
    } else {
      next.delete('q');
      next.delete('scope');
    }
    const newSearch = next.toString();
    const currentSearch = searchParams.toString();
    if (newSearch !== currentSearch) {
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchScope]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(getBackendUrl() + '/api/job-categories');
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch(getBackendUrl() + '/api/countries');
      const data = await response.json();
      setCountries(data.countries || []);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchBenchmarks = async () => {
    try {
      const response = await fetch(getBackendUrl() + '/api/salary-benchmarks');
      const data = await response.json();
      // Convertir en map pour accès rapide
      const benchmarkMap = {};
      (data.benchmarks || []).forEach(b => {
        const key = `${b.country}_${b.job_category}`;
        benchmarkMap[key] = b;
      });
      setBenchmarks(benchmarkMap);
    } catch (error) {
      console.error('Failed to fetch benchmarks:', error);
    }
  };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Filtres avancés
      if (advancedFilters.category) params.append('category', advancedFilters.category);
      if (advancedFilters.country) params.append('country', advancedFilters.country);
      if (advancedFilters.experience_level) params.append('experience_level', advancedFilters.experience_level);
      if (searchQuery) {
        params.append('search', searchQuery);
        params.append('scope', searchScope);
      }
      if (toolsFilter && toolsFilter.length > 0) {
        params.append('tools', toolsFilter.join(','));
      }

      // BUG 0 FIX (02 Mai 2026) + Directive rejetés (02 Mai 2026 soir) :
      // exclure par défaut tous les statuts finalisés ou rejetés.
      // Les rejetés sont consultables uniquement dans /admin/candidates-rejetes.
      // Les talent_pool / assigned sont visibles dans /admin/talent-pool.
      if (!showAllStatuses) {
        params.append('exclude_status', 'talent_pool,assigned,archived,rejected,rejected_auto');
      }

      // Limite haute pour avoir tous les candidats (max backend = 500)
      params.append('limit', '500');
      
      const response = await api.get(`/admin/candidates?${params.toString()}`);
      const data = await response.json();
      
      let allCandidates = data.candidates || [];
      // Total réel envoyé par le backend (peut être > allCandidates.length si > 500)
      setTotalCandidates(data.total || allCandidates.length);
      
      // Enrichir avec les noms de pays
      allCandidates = allCandidates.map(c => ({
        ...c,
        country_name: countries.find(co => co.code === c.country)?.name || c.country
      }));
      
      // Filter by evaluation status (client-side)
      if (advancedFilters.evaluation_status) {
        allCandidates = allCandidates.filter(c => {
          if (advancedFilters.evaluation_status === 'not_sent') return !c.evaluation_sent;
          if (advancedFilters.evaluation_status === 'sent_waiting') return c.evaluation_sent && !c.qualification_score;
          if (advancedFilters.evaluation_status === 'completed') return c.qualification_score > 0;
          return true;
        });
      }
      
      // Calculer les compteurs de filtres
      calculateFilterCounts(allCandidates);
      
      // Appliquer le filtre décisionnel
      const filteredCandidates = applyDecisionFilter(allCandidates, activeFilter);
      
      // Trier par priorité
      const sortedCandidates = sortByPriority(filteredCandidates);
      
      setCandidates(sortedCandidates);
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
      toast.error('Erreur lors du chargement des candidats');
    } finally {
      setLoading(false);
    }
  };

  // === FILTER LOGIC ===
  const calculateFilterCounts = (allCandidates) => {
    const now = new Date();
    const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
    
    const counts = {
      all: allCandidates.length,
      new: allCandidates.filter(c => new Date(c.created_at) > oneDayAgo).length,
      recommended: allCandidates.filter(c => {
        const score = c.qualification?.score_total || c.ai_analysis?.candidate_score || 0;
        const isQualified = c.qualification?.decision === 'A_RECRUTER' || 
                          c.status === 'validated' || 
                          c.status === 'prequalified';
        return score >= 75 && isQualified;
      }).length,
      priority: allCandidates.filter(c => {
        const isNew = new Date(c.created_at) > oneDayAgo;
        const score = c.qualification?.score_total || c.ai_analysis?.candidate_score || 0;
        const isRecommended = score >= 75;
        return (isNew && isRecommended) || c.availability === 'immediate';
      }).length,
      budgetOk: allCandidates.filter(c => {
        const salary = c.expected_salary || 0;
        // Budget OK si salaire <= 1000€ (seuil arbitraire, à ajuster)
        return salary > 0 && salary <= 1000;
      }).length
    };
    
    setFilterCounts(counts);
  };

  const applyDecisionFilter = (allCandidates, filter) => {
    const now = new Date();
    const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
    
    switch (filter) {
      case 'new':
        return allCandidates.filter(c => new Date(c.created_at) > oneDayAgo);
        
      case 'recommended':
        return allCandidates.filter(c => {
          const score = c.qualification?.score_total || c.ai_analysis?.candidate_score || 0;
          const isQualified = c.qualification?.decision === 'A_RECRUTER' || 
                            c.status === 'validated' || 
                            c.status === 'prequalified';
          return score >= 75 && isQualified;
        });
        
      case 'priority':
        return allCandidates.filter(c => {
          const isNew = new Date(c.created_at) > oneDayAgo;
          const score = c.qualification?.score_total || c.ai_analysis?.candidate_score || 0;
          const isRecommended = score >= 75;
          return (isNew && isRecommended) || c.availability === 'immediate';
        });
        
      case 'budgetOk':
        return allCandidates.filter(c => {
          const salary = c.expected_salary || 0;
          return salary > 0 && salary <= 1000;
        });
        
      default:
        return allCandidates;
    }
  };

  const sortByPriority = (candidatesList) => {
    const now = new Date();
    const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
    
    return [...candidatesList].sort((a, b) => {
      // 1. Nouveaux en premier
      const aIsNew = new Date(a.created_at) > oneDayAgo;
      const bIsNew = new Date(b.created_at) > oneDayAgo;
      if (aIsNew && !bIsNew) return -1;
      if (!aIsNew && bIsNew) return 1;
      
      // 2. Par score décroissant
      const aScore = a.qualification?.score_total || a.ai_analysis?.candidate_score || 0;
      const bScore = b.qualification?.score_total || b.ai_analysis?.candidate_score || 0;
      if (aScore !== bScore) return bScore - aScore;
      
      // 3. Par date de création (plus récent en premier)
      return new Date(b.created_at) - new Date(a.created_at);
    });
  };

  // === ACTIONS ===
  
  // Bulk selection helpers
  const candidatesWithoutEval = candidates.filter(c => !c.qualification_score);
  const allWithoutEvalSelected = candidatesWithoutEval.length > 0 && candidatesWithoutEval.every(c => selectedIds.has(c.id));
  
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  
  const toggleSelectAll = () => {
    if (allWithoutEvalSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(candidatesWithoutEval.map(c => c.id)));
    }
  };
  
  const sendBulkEvaluation = async () => {
    if (selectedIds.size === 0) return;
    setSendingBulk(true);
    try {
      const res = await api.post('/admin/qualification/send-evaluation-bulk', {
        candidate_ids: Array.from(selectedIds)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setSelectedIds(new Set());
        fetchCandidates();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de l'envoi");
      }
    } catch (err) {
      toast.error("Erreur de connexion");
    } finally {
      setSendingBulk(false);
    }
  };

  const handleKeep = useCallback(async (candidate) => {
    setAnimatingId(candidate.id);
    setAnimationType('keep');

    try {
      // Choix produit (02 Mai 2026) : "Garder" = direct Talent Pool en 1 clic.
      // On appelle l'endpoint canonique POST /keep qui passe désormais par
      // KEEP_TRANSITIONS → talent_pool depuis tout statut non-finalisé.
      const response = await api.post(`/admin/candidates/${candidate.id}/keep`);

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const newStatus = data.status_new || 'talent_pool';
        const onboardingOk = data.onboarding?.ok === true;
        toast.success(
          `${candidate.first_name} ajouté au Talent Pool`,
          onboardingOk ? { description: "Email d'onboarding envoyé au candidat" } : undefined,
        );

        // Retirer immédiatement le candidat de la liste locale (disparaît visuellement)
        // puisque le filtre `exclude_status` est actif par défaut.
        setTimeout(() => {
          if (showAllStatuses) {
            // Mode "Voir tous" — on met juste à jour le statut
            setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, status: newStatus } : c));
          } else {
            // Mode par défaut — retrait complet du state
            setCandidates(prev => prev.filter(c => c.id !== candidate.id));
          }
          setAnimatingId(null);
          setAnimationType(null);
        }, 300);
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error keeping candidate:', error);
      toast.error('Erreur lors de la mise à jour');
      setAnimatingId(null);
      setAnimationType(null);
    }
  }, [showAllStatuses]);

  const handlePass = useCallback(async (candidate) => {
    setAnimatingId(candidate.id);
    setAnimationType('pass');

    try {
      const response = await api.post(`/admin/candidates/${candidate.id}/reject`);

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const emailOk = data?.rejection_email?.ok === true;
        // Choix produit (02 Mai 2026 soir) : email bienveillant envoyé au candidat.
        toast.success(
          emailOk ? 'Candidat rejeté · email envoyé' : 'Candidat rejeté · email non envoyé',
          { duration: 2500 },
        );

        setTimeout(() => {
          if (showAllStatuses) {
            setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, status: 'rejected', rejected_at: new Date().toISOString() } : c));
          } else {
            // Retrait immédiat : les rejetés ne sont plus dans la liste principale
            setCandidates(prev => prev.filter(c => c.id !== candidate.id));
          }
          setAnimatingId(null);
          setAnimationType(null);
        }, 300);
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error rejecting candidate:', error);
      toast.error('Erreur lors du rejet');
      setAnimatingId(null);
      setAnimationType(null);
    }
  }, [showAllStatuses]);

  const handleView = useCallback((candidate) => {
    navigate(`/admin/candidates/${candidate.id}`);
  }, [navigate]);

  // === GET BENCHMARK FOR CANDIDATE ===
  const getBenchmarkForCandidate = (candidate) => {
    const key = `${candidate.country}_${candidate.primary_category || candidate.job_category}`;
    return benchmarks[key] || null;
  };

  // === RENDER ===
  // Les rejetés sont désormais filtrés côté backend (exclude_status=rejected,rejected_auto)
  // et consultables sur la page /admin/candidates-rejetes.
  const activeCandidates = candidates;
  const remainingCount = activeCandidates.length;
  const totalToProcess = filterCounts.all;
  const truncated = totalCandidates > candidates.length;
  const searchActive = !!searchQuery.trim();

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="candidates-page">
      {/* === HEADER === */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-tp-text">
            Candidats
          </h1>
          <p className="text-tp-text-light text-sm md:text-base">
            {searchActive ? (
              <>
                <strong className="text-tp-text">{remainingCount}</strong> résultat{remainingCount > 1 ? 's' : ''} pour
                <em className="text-tp-text"> « {searchQuery} »</em>
              </>
            ) : remainingCount > 0 ? (
              <>
                {remainingCount} candidat{remainingCount > 1 ? 's' : ''} actif{remainingCount > 1 ? 's' : ''}
              </>
            ) : (
              'Aucun candidat actif'
            )}
            {truncated && (
              <span className="text-orange-600 ml-1">
                · {totalCandidates} au total (affinez la recherche)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <Link
            to="/admin/assignment-history"
            className="text-xs sm:text-sm text-tp-accent hover:underline inline-flex items-center gap-1.5"
            data-testid="assignment-history-link"
          >
            <History className="h-4 w-4" />
            Historique des assignations
          </Link>
          <button
            type="button"
            onClick={() => setShowAllStatuses((v) => !v)}
            className="text-xs sm:text-sm text-gray-600 hover:text-tp-primary inline-flex items-center gap-1.5 border border-gray-200 rounded-md px-2 py-1 hover:border-gray-300"
            data-testid="toggle-show-all-statuses"
            title={showAllStatuses ? 'Masquer les candidats en Talent Pool / assignés' : 'Afficher aussi les candidats en Talent Pool / assignés'}
          >
            {showAllStatuses ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showAllStatuses ? 'Masquer finalisés' : 'Voir tous'}
          </button>
          <div className="inline-flex border border-gray-200 rounded-md p-0.5 bg-white" data-testid="view-mode-switcher">
            <button
              type="button"
              onClick={() => {
                setViewMode('cards');
                sessionStorage.setItem('candidates_view_mode', 'cards');
              }}
              className={`px-2.5 py-1 rounded text-xs inline-flex items-center gap-1 transition-colors ${
                viewMode === 'cards' ? 'bg-tp-primary text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="view-mode-cards"
              aria-label="Vue Liste"
            >
              <List className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Liste</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('kanban');
                sessionStorage.setItem('candidates_view_mode', 'kanban');
              }}
              className={`px-2.5 py-1 rounded text-xs inline-flex items-center gap-1 transition-colors ${
                viewMode === 'kanban' ? 'bg-tp-primary text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="view-mode-kanban"
              aria-label="Vue Kanban"
            >
              <Columns className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Kanban</span>
            </button>
          </div>
          <CVReanalyzeButton onComplete={fetchCandidates} />
        </div>
      </div>

      {/* === PROGRESS INDICATOR === */}
      {totalToProcess > 0 && (
        <div className="mb-4 bg-gray-100 rounded-full h-2 overflow-hidden">
          <div 
            className="bg-green-500 h-full transition-all duration-500"
            style={{ width: `${((totalToProcess - remainingCount) / totalToProcess) * 100}%` }}
          />
        </div>
      )}

      {/* === FILTER CHIPS === */}
      <div className="mb-4">
        <FilterChips
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          counts={filterCounts}
        />
      </div>

      {/* === SEARCH + ADVANCED FILTERS === */}
      <Card className="p-4 mb-6 border-tp-border">
        {/* Category chips - quick filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="category-chips">
            <button
              onClick={() => setAdvancedFilters({ ...advancedFilters, category: '' })}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                !advancedFilters.category
                  ? 'bg-[#0F2744] text-white border-[#0F2744]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
              data-testid="chip-all-categories"
            >
              Toutes
            </button>
            {categories.filter(c => c.is_active !== false && c._id !== 'autre').map(cat => {
              const active = advancedFilters.category === cat._id;
              const color = cat.color || '#0F2744';
              return (
                <button
                  key={cat._id}
                  onClick={() => setAdvancedFilters({
                    ...advancedFilters,
                    category: active ? '' : cat._id,
                  })}
                  className="text-xs px-3 py-1.5 rounded-full border transition-all"
                  style={{
                    backgroundColor: active ? color : `${color}14`,
                    color: active ? '#fff' : color,
                    borderColor: active ? color : `${color}33`,
                  }}
                  data-testid={`chip-category-${cat._id}`}
                >
                  {cat.label_fr || cat.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-tp-text-light" />
          <Input
            placeholder="Rechercher (ex. Media Buyer, n8n, Claude, Funnel Builder…)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-candidates"
          />
          {searchQuery && (
            <p className="text-[11px] text-tp-text-light mt-1">
              Recherche dans : nom · email · poste · compétences · catégories · résumé IA · ville/pays
            </p>
          )}
        </div>

        {/* Tools filter — multi-select facetté */}
        <div className="mt-3">
          <ToolsFilter value={toolsFilter} onChange={setToolsFilter} />
        </div>

        {/* Toggle advanced filters */}
        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className="flex items-center gap-2 text-sm text-tp-text-light hover:text-tp-text"
        >
          {showAdvancedFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          Filtres avancés
        </button>

        {/* Advanced filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-100">
            <Select 
              value={advancedFilters.category || 'all'} 
              onValueChange={(v) => setAdvancedFilters(prev => ({ ...prev, category: v === 'all' ? '' : v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={advancedFilters.country || 'all'} 
              onValueChange={(v) => setAdvancedFilters(prev => ({ ...prev, country: v === 'all' ? '' : v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pays" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous pays</SelectItem>
                {countries.map(country => (
                  <SelectItem key={country.code} value={country.code}>{country.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={advancedFilters.experience_level || 'all'} 
              onValueChange={(v) => setAdvancedFilters(prev => ({ ...prev, experience_level: v === 'all' ? '' : v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Niveau" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous niveaux</SelectItem>
                <SelectItem value="junior">Junior</SelectItem>
                <SelectItem value="intermediaire">Confirmé</SelectItem>
                <SelectItem value="senior">Senior</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={advancedFilters.evaluation_status || 'all'} 
              onValueChange={(v) => setAdvancedFilters(prev => ({ ...prev, evaluation_status: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid="filter-evaluation-status">
                <SelectValue placeholder="Évaluation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes évaluations</SelectItem>
                <SelectItem value="not_sent">Non envoyée</SelectItem>
                <SelectItem value="sent_waiting">Envoyée (en attente)</SelectItem>
                <SelectItem value="completed">Complétée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </Card>

      {/* === BULK ACTIONS BAR === */}
      {candidatesWithoutEval.length > 0 && (
        <div className="flex items-center justify-between bg-[#0F2744]/5 border border-[#0F2744]/15 rounded-lg px-4 py-3 mb-4">
          <div className="flex items-center gap-3">
            <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-[#0F2744] hover:text-[#0F2744]/80" data-testid="select-all-btn">
              {allWithoutEvalSelected ? <CheckSquare className="h-5 w-5 text-[#4C8F6E]" /> : <Square className="h-5 w-5" />}
              {selectedIds.size > 0 ? `${selectedIds.size} sélectionné(s)` : `Sélectionner tout (${candidatesWithoutEval.length})`}
            </button>
          </div>
          {selectedIds.size > 0 && (
            <Button 
              onClick={sendBulkEvaluation} 
              disabled={sendingBulk}
              className="bg-[#4C8F6E] hover:bg-[#3d7a5c] text-white"
              data-testid="send-bulk-eval-btn"
            >
              {sendingBulk ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Envoyer le formulaire à {selectedIds.size} candidat(s)
            </Button>
          )}
        </div>
      )}

      {/* === CANDIDATES LIST (ACTIFS) === */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
        </div>
      ) : activeCandidates.length > 0 ? (
        viewMode === 'kanban' ? (
          <KanbanView
            candidates={activeCandidates}
            onMutated={(candidateId, newStatus) => {
              // Retrait immédiat si le nouveau statut est finalisé et qu'on n'affiche pas tout
              const isFinal = ['talent_pool', 'assigned', 'rejected', 'rejected_auto', 'archived'].includes(newStatus);
              if (!showAllStatuses && isFinal) {
                setCandidates((prev) => prev.filter((c) => c.id !== candidateId));
              } else {
                setCandidates((prev) => prev.map((c) => (c.id === candidateId ? { ...c, status: newStatus } : c)));
              }
            }}
          />
        ) : (
          <div className="space-y-4">
            {activeCandidates.map((candidate) => (
              <div key={candidate.id} className="flex gap-3 items-start">
                {/* Checkbox */}
                {!candidate.qualification_score && (
                  <button
                    onClick={() => toggleSelect(candidate.id)}
                    className="mt-4 flex-shrink-0"
                    data-testid={`checkbox-${candidate.id}`}
                  >
                    {selectedIds.has(candidate.id)
                      ? <CheckSquare className="h-5 w-5 text-[#4C8F6E]" />
                      : <Square className="h-5 w-5 text-gray-300 hover:text-gray-500" />
                    }
                  </button>
                )}
                <div className="flex-1">
                  <DecisionCard
                    key={candidate.id}
                    candidate={candidate}
                    benchmark={getBenchmarkForCandidate(candidate)}
                    onKeep={() => handleKeep(candidate)}
                    onPass={() => handlePass(candidate)}
                    onView={() => handleView(candidate)}
                    isAnimatingOut={animatingId === candidate.id}
                    animationType={animationType}
                    categories={categories}
                  />
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <Card className="p-12 text-center border-tp-border">
          <CheckCircle2 className="h-16 w-16 text-[#4C8F6E] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-tp-text mb-2">
            Tous les candidats traités !
          </h3>
          <p className="text-tp-text-light mb-4">
            {activeFilter !== 'all' 
              ? 'Aucun candidat ne correspond à ce filtre.'
              : 'Vous avez traité tous les candidats en attente.'
            }
          </p>
          {activeFilter !== 'all' && (
            <Button 
              variant="outline"
              onClick={() => setActiveFilter('all')}
            >
              Voir tous les candidats
            </Button>
          )}
        </Card>
      )}

      {/* Section "Rejetés" supprimée — voir page dédiée /admin/candidates-rejetes */}
    </div>
  );
};

export default CandidatesPage;
